import React from "react";
import { Sun, Moon, TreePine, Sparkles } from "lucide-react";
import { SectionHeading, PillLink } from "../components/common";
import { GuideMascot, GuideWithNote } from "../components/GuideMascot";
import { LOOP_VALUES, GUIDES, PHOTOS } from "../data/content";

const DayWithUs = () => {
  return (
    <div data-testid="day-page">
      <section className="bg-[#FDFBF7] pt-14 pb-16">
        <div className="mx-auto max-w-5xl px-5 md:px-8 text-center">
          <span className="font-hand text-2xl text-[#4A90E2]">Gentle rhythms, not rigid clocks</span>
          <h1 className="font-head font-700 tracking-tight text-4xl sm:text-5xl lg:text-6xl text-[#1E293B] mt-1">A Day With Us</h1>
          <p className="mt-4 font-body text-lg text-[#475569] leading-relaxed max-w-2xl mx-auto">
            Instead of a rigid, hour-by-hour curriculum, Divinities Cove uses a flexible loop schedule that lets children follow their energy, interests, and natural rhythms.
          </p>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="mx-auto max-w-6xl px-5 md:px-8 grid lg:grid-cols-2 gap-12 items-center">
          <div className="relative">
            <img src={PHOTOS.binoculars} alt="Child exploring with binoculars" className="w-full aspect-[4/3] object-cover blob-mask-2 shadow-soft" />
            <div className="absolute -bottom-6 -right-4 animate-floaty"><GuideMascot src={GUIDES.book} size={104} ring="#4A90E2" /></div>
          </div>
          <div>
            <SectionHeading eyebrow="Our Loop Schedule" title="Freedom within a rhythm" sub="Meals, movement, creative projects, outdoor learning, quiet time, stories, music, cultural experiences, and practical life skills all flow naturally throughout the day. We prioritize:" />
            <div className="mt-6 grid sm:grid-cols-2 gap-3">
              {LOOP_VALUES.map((v) => (
                <div key={v} className="flex items-start gap-2 font-body text-[#1E293B] bg-[#eaf2fc] rounded-2xl px-4 py-3" data-testid="loop-value">
                  <Sparkles size={18} className="text-[#4A90E2] mt-0.5 shrink-0" /><span className="text-sm">{v}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* FRIDAY highlight */}
      <section className="py-20 bg-[#84A98C]">
        <div className="mx-auto max-w-6xl px-5 md:px-8">
          <div className="grid lg:grid-cols-2 gap-10 items-center">
            <div className="text-white">
              <span className="inline-flex items-center gap-2 bg-white/20 rounded-full px-4 py-2 font-body font-600 text-sm">
                <TreePine size={18} /> Friday Field Trips
              </span>
              <h2 className="mt-4 font-head font-700 tracking-tight text-3xl sm:text-4xl">Fridays are for the outdoors</h2>
              <p className="mt-4 font-body text-lg text-white/90 leading-relaxed">
                Every Friday is our field trip and outdoors day — nature walks, adventures, and exploration in the world beyond our walls.
              </p>
              <div className="mt-5 bg-white/15 rounded-3xl p-6">
                <p className="font-hand text-2xl text-[#FFD166]">"But what if my child wants to stay in?"</p>
                <p className="mt-2 font-body text-white/90 leading-relaxed">
                  That's completely okay! This is unschooling — we never force participation. Any child who'd rather stay in and play, read, create, or rest is always welcome to do so. We honor each child's choice, energy, and natural rhythm. Learning happens either way. 🌿
                </p>
              </div>
            </div>
            <div className="flex justify-center">
              <GuideWithNote src={GUIDES.leaf} ring="#FFD166" size={150} note="Let's go explore outside! Or stay & play — your choice! 🍃" />
            </div>
          </div>
        </div>
      </section>

      {/* Rhythm strip */}
      <section className="py-16 bg-white">
        <div className="mx-auto max-w-5xl px-5 md:px-8">
          <SectionHeading center eyebrow="From sunrise to evening" title="A gentle flow to the day" />
          <div className="mt-10 grid sm:grid-cols-3 gap-6">
            {[
              { icon: Sun, label: "Morning", desc: "Warm welcomes, breakfast, and long stretches of uninterrupted play & discovery.", tint: "#fdf1e6" },
              { icon: TreePine, label: "Midday", desc: "Outdoor exploration, gardening, hands-on projects, a fresh lunch, and rest when needed.", tint: "#eef4ef" },
              { icon: Moon, label: "Evening", desc: "Stories, music, creative expression, dinner, and a calm close to the day.", tint: "#eaf2fc" },
            ].map((s) => (
              <div key={s.label} className="rounded-3xl p-7 text-center shadow-soft-sm" style={{ background: s.tint }} data-testid="rhythm-block">
                <span className="w-14 h-14 mx-auto rounded-2xl bg-white grid place-items-center text-[#4A90E2] shadow-soft-sm"><s.icon size={24} /></span>
                <h3 className="mt-4 font-head font-600 text-xl text-[#1E293B]">{s.label}</h3>
                <p className="mt-2 font-body text-sm text-[#475569] leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
          <div className="mt-12 text-center"><PillLink to="/meals" variant="primary" testid="day-to-meals">See What's on the Menu</PillLink></div>
        </div>
      </section>
    </div>
  );
};

export default DayWithUs;
